/**
 * theme.ts
 * Material-UI theme configuration with light and dark modes.
 * Dark mode matches the landing page aesthetic with glassmorphism and gradients.
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';

// ============================================================================
// SHARED THEME OPTIONS (applies to both light and dark)
// ============================================================================
const sharedThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: '"Montserrat", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      textTransform: 'uppercase',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      textTransform: 'uppercase',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    button: {
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    // Remove hard-coded colors from typography - let palette handle it
  },
};

// ============================================================================
// LIGHT THEME (Original)
// ============================================================================
export const lightTheme = createTheme({
  ...sharedThemeOptions,
  palette: {
    mode: 'light',
    primary: {
      main: '#E53935', // Brand red
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#212121', // Charcoal
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFAFA', // Off-white background
      paper: '#FFFFFF',
    },
    text: {
      primary: '#212121', // Charcoal for primary text
      secondary: '#666666',
    },
    success: {
      main: '#43A047', // Green accent
    },
    warning: {
      main: '#FFC107', // Amber accent
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          padding: '12px 24px',
        },
        contained: {
          boxShadow: '0 4px 12px rgba(229, 57, 53, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(229, 57, 53, 0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          border: '1px solid #F0F0F0',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#E53935',
          '&.Mui-checked': {
            color: '#E53935',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#E53935',
          '&.Mui-checked': {
            color: '#E53935',
          },
        },
      },
    },
  },
});

// ============================================================================
// DARK THEME (Landing Page Aesthetic)
// ============================================================================
export const darkTheme = createTheme({
  ...sharedThemeOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: '#E53935', // Brand red - pops beautifully on dark
      light: '#EF5350',
      dark: '#C62828',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1A1A2E', // Deep navy (from landing page)
      light: '#16213E',
      dark: '#0D0D0D',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#0D0D0D', // Deep black (landing page bg)
      paper: '#1A1A2E', // Dark navy for surfaces
    },
    text: {
      primary: '#FFFFFF', // White for primary text
      secondary: 'rgba(255, 255, 255, 0.7)', // 70% white for secondary
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    success: {
      main: '#43A047', // Green accent
      light: '#66BB6A',
      dark: '#2E7D32',
    },
    warning: {
      main: '#FFC107', // Amber/gold accent
      light: '#FFD700',
      dark: '#FFA000',
    },
    error: {
      main: '#E53935',
      light: '#EF5350',
      dark: '#C62828',
    },
    info: {
      main: '#1976D2', // Blue accent
      light: '#42A5F5',
      dark: '#1565C0',
    },
    divider: 'rgba(255, 255, 255, 0.1)', // Subtle dividers
    action: {
      active: 'rgba(255, 255, 255, 0.8)',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(229, 57, 53, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Custom scrollbar styling (from landing page)
          '&::-webkit-scrollbar': {
            width: '12px',
            height: '12px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(180deg, #E53935 0%, #C62828 100%)',
            borderRadius: '10px',
            border: '2px solid rgba(0, 0, 0, 0.3)',
            '&:hover': {
              background: 'linear-gradient(180deg, #EF5350 0%, #E53935 100%)',
            },
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#E53935 rgba(255, 255, 255, 0.03)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          padding: '12px 24px',
        },
        contained: {
          background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
          boxShadow: '0 4px 20px rgba(229, 57, 53, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
            boxShadow: '0 6px 25px rgba(229, 57, 53, 0.5)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)',
          color: '#FFFFFF',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          '&:hover': {
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.12) 100%)',
            boxShadow: '0 6px 25px rgba(0, 0, 0, 0.4)',
          },
        },
        containedInfo: {
          background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
          color: '#FFFFFF',
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #42A5F5 0%, #1976D2 100%)',
            boxShadow: '0 6px 25px rgba(25, 118, 210, 0.5)',
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.3)',
          color: '#FFFFFF',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.6)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove MUI's default overlay
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
        elevation1: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        },
        elevation2: {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#E53935',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.6)',
          },
          '& .MuiOutlinedInput-input': {
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E53935',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.05)',
          color: '#FFFFFF',
        },
        head: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)', // Clean subtle grey, not brown!
          fontWeight: 700,
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: '#FFFFFF', // Bright white for contrast
          borderBottom: '2px solid rgba(229, 57, 53, 0.4)', // Red accent line
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.5)',
          '&.Mui-checked': {
            color: '#E53935',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.5)',
          '&.Mui-checked': {
            color: '#E53935',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#E53935',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#E53935',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          // Let variants handle their own backgrounds
        },
        filled: {
          // Default filled style
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          color: '#FFFFFF',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        },
        outlined: {
          backgroundColor: 'transparent',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          color: '#FFFFFF',
        },
        // Color variants - using proper MUI class names
        colorSuccess: {
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(67, 160, 71, 0.25)',
            color: '#66BB6A',
            border: '1px solid rgba(67, 160, 71, 0.5)',
          },
          '&.MuiChip-outlined': {
            backgroundColor: 'transparent',
            borderColor: 'rgba(67, 160, 71, 0.5)',
            color: '#66BB6A',
          },
        },
        colorError: {
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(229, 57, 53, 0.25)',
            color: '#EF5350',
            border: '1px solid rgba(229, 57, 53, 0.5)',
          },
          '&.MuiChip-outlined': {
            backgroundColor: 'transparent',
            borderColor: 'rgba(229, 57, 53, 0.5)',
            color: '#EF5350',
          },
        },
        colorWarning: {
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(255, 193, 7, 0.25)',
            color: '#FFD700',
            border: '1px solid rgba(255, 193, 7, 0.5)',
          },
          '&.MuiChip-outlined': {
            backgroundColor: 'transparent',
            borderColor: 'rgba(255, 193, 7, 0.5)',
            color: '#FFD700',
          },
        },
        colorInfo: {
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(25, 118, 210, 0.25)',
            color: '#42A5F5',
            border: '1px solid rgba(25, 118, 210, 0.5)',
          },
          '&.MuiChip-outlined': {
            backgroundColor: 'transparent',
            borderColor: 'rgba(25, 118, 210, 0.5)',
            color: '#42A5F5',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(145deg, #1A1A2E 0%, #16213E 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(26, 26, 46, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          fontSize: '0.875rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          border: '1px solid',
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: 'rgba(67, 160, 71, 0.15)',
          borderColor: 'rgba(67, 160, 71, 0.3)',
          color: '#66BB6A',
        },
        standardError: {
          backgroundColor: 'rgba(229, 57, 53, 0.15)',
          borderColor: 'rgba(229, 57, 53, 0.3)',
          color: '#EF5350',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 193, 7, 0.15)',
          borderColor: 'rgba(255, 193, 7, 0.3)',
          color: '#FFD700',
        },
        standardInfo: {
          backgroundColor: 'rgba(25, 118, 210, 0.15)',
          borderColor: 'rgba(25, 118, 210, 0.3)',
          color: '#42A5F5',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(145deg, rgba(26, 26, 46, 0.98) 0%, rgba(22, 33, 62, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(229, 57, 53, 0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(229, 57, 53, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(229, 57, 53, 0.25)',
            },
          },
        },
      },
    },
  },
});

